import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { validateWalletBalance, clearError } from '../../redux/slices/walletSlice';
import './PaymentValidation.css';

interface PaymentValidationProps {
  amount: number;
  onValidationComplete?: (isValid: boolean) => void;
  showIcon?: boolean;
  className?: string;
}

const PaymentValidation: React.FC<PaymentValidationProps> = ({
  amount,
  onValidationComplete,
  showIcon = true,
  className = ''
}) => {
  const dispatch = useDispatch();
  const [isChecking, setIsChecking] = useState(false);
  const { balanceValidation, error } = useSelector(
    (state: RootState) => state.wallet
  );

  const checkBalanceHandler = useCallback(async () => {
    setIsChecking(true);
    try {
      const result = await dispatch(validateWalletBalance(amount) as any);
      if (result.payload) {
        onValidationComplete?.(result.payload.isValid);
      }
    } finally {
      setIsChecking(false);
    }
  }, [amount, dispatch, onValidationComplete]);

  React.useEffect(() => {
    checkBalanceHandler();
  }, [amount, checkBalanceHandler]);

  if (isChecking && !balanceValidation) {
    return (
      <div className={`payment-validation validating ${className}`}>
        <div className="spinner-small"></div>
        <p>Checking balance...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`payment-validation error ${className}`}>
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <p className="error-message">{error}</p>
          <button 
            className="retry-btn"
            onClick={() => {
              dispatch(clearError());
              checkBalanceHandler();
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!balanceValidation) {
    return null;
  }

  const { isValid, currentBalance, requiredAmount, message } = balanceValidation;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className={`payment-validation ${isValid ? 'valid' : 'invalid'} ${className}`}>
      <div className="validation-content">
        {showIcon && (
          <span className={`validation-icon ${isValid ? 'success' : 'error'}`}>
            {isValid ? '✓' : '✕'}
          </span>
        )}

        <div className="validation-info">
          <p className="validation-message">
            {isValid ? (
              <>
                <strong>Balance Check Passed</strong>
                <br />
                <small>You have sufficient balance to proceed with this payment</small>
              </>
            ) : (
              <>
                <strong>Insufficient Balance</strong>
                <br />
                <small>{message}</small>
              </>
            )}
          </p>

          {!isValid && (
            <div className="balance-details">
              <div className="detail-item">
                <span className="label">Required Amount:</span>
                <span className="value required">{formatCurrency(requiredAmount)}</span>
              </div>
              <div className="detail-item">
                <span className="label">Current Balance:</span>
                <span className="value current">{formatCurrency(currentBalance)}</span>
              </div>
              <div className="detail-item shortage">
                <span className="label">Shortage:</span>
                <span className="value shortage">
                  {formatCurrency(requiredAmount - currentBalance)}
                </span>
              </div>
            </div>
          )}

          {!isValid && (
            <div className="action-buttons">
              <button className="fund-btn" onClick={() => globalThis.location.href = '/dashboard/funding'}>
                💰 Fund Account
              </button>
              <p className="help-text">Fund your account to proceed with this payment</p>
            </div>
          )}
        </div>
      </div>

      {isValid && (
        <div className="validation-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentValidation;
